import { useState } from "react";
import { useTranslation } from "react-i18next";
import { umamiEvent } from "../lib/analytics";
import { getThemeColors } from "../lib/theme";

const dismissalKey = "hideGeoBingoRecommendation";

interface GeoBingoCardProps {
  isDark: boolean;
  variant: "game" | "daily" | "openlobby";
}

export function GeoBingoCard({ isDark, variant }: GeoBingoCardProps) {
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(dismissalKey) === "1",
  );
  const isDaily = variant === "daily";
  const href = isDaily
    ? "https://geobingo.io/?utm_source=wikiparty&utm_medium=referral&utm_campaign=cross_game&utm_content=daily_results"
    : "https://geobingo.io/?utm_source=wikiparty&utm_medium=referral&utm_campaign=cross_game&utm_content=multiplayer_results";

  if (dismissed) return null;

  const dismiss = () => {
    window.localStorage.setItem(dismissalKey, "1");
    setDismissed(true);
  };

  return (
    <aside
      data-testid="geobingo-card"
      className={`p-3 sm:px-4 sm:py-3 ${theme.panel}`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h2
            className={`font-display text-sm sm:text-base ${theme.text}`}
          >
            <span
              className={`mr-1 font-sans font-bold ${theme.textMuted}`}
            >
              {t("crossGame.label")}:
            </span>
            {isDaily ? t("crossGame.dailyTitle") : "GeoBingo"}
          </h2>
          <p
            className={`text-xs sm:text-sm ${theme.textMuted}`}
          >
            {isDaily
              ? t("crossGame.dailyDescription")
              : t("crossGame.description")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <a
            data-testid="geobingo-link"
            href={href}
            {...umamiEvent("geobingo-try", { variant })}
            className={`flex-1 ${theme.buttonSmall}`}
          >
            {isDaily ? t("crossGame.dailyAction") : t("crossGame.action")}
          </a>
          <button
            type="button"
            data-testid="dismiss-geobingo-card"
            onClick={dismiss}
            aria-label={t("crossGame.dismiss")}
            title={t("crossGame.dismiss")}
            className={`h-8 w-8 p-0! text-base ${theme.buttonSmall}`}
          >
            ×
          </button>
        </div>
      </div>
    </aside>
  );
}
